import {
  Banner,
  Button,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Input,
  VStack,
} from '@designSystem';
import { useEffect } from 'react';
import { FieldValues, SubmitHandler, useForm } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';
import { MainContent } from 'src/components/mainContent';
import { definedIntlMessages } from 'src/config/constants/definedIntlMessages';
import InfoIcon from 'src/designSystem/icons/info.svg';
import ErrorIcon from 'src/designSystem/icons/error.svg';
import { emailCacheKey } from 'src/config/constants/shared';
import { isClientRuntime } from 'src/lib/utils/isClientRuntime';

export type SigninPageError = {
  /** The error title */
  title: string;
  /** The error description */
  description?: string;
};

export type SigninPageProps = {
  /** If the login token is invalid */
  isInvalidToken: boolean;
  /** On the signin form submission */
  onSubmit: SubmitHandler<FieldValues>;
  /** If the form is submitting */
  isSubmitting: boolean;
  /** Error object to render in banner */
  error?: SigninPageError;
};

export const SigninPage = ({
  isInvalidToken,
  onSubmit,
  isSubmitting,
  error,
}: SigninPageProps): JSX.Element => {
  const intl = useIntl();

  const {
    setFocus,
    setValue,
    register,
    handleSubmit,
    formState: { errors },
  } = useForm();

  useEffect(() => {
    // Focus the email field on init
    setFocus('email');

    // Set the email value from the cache if present
    if (isClientRuntime()) {
      const cachedEmail = window.localStorage.getItem(emailCacheKey);

      if (cachedEmail) {
        setValue('email', cachedEmail);
      }
    }
  }, [setFocus, setValue]);

  const getBanner = () => {
    if (isInvalidToken && !error) {
      return (
        <Banner
          role="alert"
          icon={<InfoIcon />}
          title={intl.formatMessage(definedIntlMessages.invalidTokenTitle)}
          description={intl.formatMessage(
            definedIntlMessages.invalidTokenDescription,
          )}
          data-testid="invalidTokenText"
        />
      );
    }

    if (error) {
      return (
        <Banner
          role="alert"
          colorScheme="red"
          icon={<ErrorIcon />}
          title={error.title}
          description={error.description}
        />
      );
    }
  };

  return (
    <MainContent
      title={<FormattedMessage defaultMessage="Log in to Dixa" />}
      metaTitle={intl.formatMessage(definedIntlMessages.pageSigninTitle)}
      metaDescription={intl.formatMessage(
        definedIntlMessages.defaultMetaDescription,
      )}
      lead={
        <FormattedMessage defaultMessage="Enter your email and we’ll send a magic link to log in." />
      }
      banner={getBanner()}
    >
      <VStack
        as="form"
        align="stretch"
        spacing="4"
        onSubmit={handleSubmit(onSubmit)}
        autoComplete="on"
        aria-label="Sign in form"
        data-testid="signin-form"
        noValidate
      >
        <FormControl isInvalid={errors?.email}>
          <FormLabel>
            <FormattedMessage defaultMessage="Email" />
          </FormLabel>
          <Input
            type="email"
            autoComplete="email"
            disabled={isSubmitting}
            aria-required={true}
            {...register('email', {
              required: {
                value: true,
                message: intl.formatMessage(
                  definedIntlMessages.invalidEmailRequired,
                ),
              },
              pattern: {
                value: /\S+@\S+\.\S+/,
                message: intl.formatMessage(
                  definedIntlMessages.invalidEmailPattern,
                ),
              },
            })}
          />
          <FormErrorMessage>{errors?.email?.message}</FormErrorMessage>
        </FormControl>
        <Button
          type="submit"
          width="100%"
          isLoading={isSubmitting}
          colorScheme="purple"
        >
          <FormattedMessage defaultMessage="Sign in" />
        </Button>
      </VStack>
    </MainContent>
  );
};
